import { postUrl } from '@/apiCalls/fetch';
import { ISupportingDoc, setSupportingDocs } from '@/context/borrower';
import { store } from '@/context/store';

import IDocument from './uploadDocumentTypes';

interface IPresignResponse {
    uploadUrl: string;
    key: string;
}

export async function postDocument(
    code: string,
    files: File[]
): Promise<IDocument[]> {
    const uploaded: IDocument[] = [];

    // Upload each file directly to S3 via a presigned URL. This bypasses the
    // API/Lambda, so there is no request-size limit on the upload itself.
    for (const file of files) {
        // 1. Ask our API for a short-lived presigned S3 PUT URL.
        const { uploadUrl, key } = await postUrl<IPresignResponse>(
            '/api/documents/presign',
            { filename: file.name, code }
        );

        // 2. Upload the file straight to S3. Use the native fetch (NOT the axios
        //    httpClient) so the app's Bearer token is never sent to S3.
        const s3Response = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type || 'application/octet-stream',
            },
        });

        if (!s3Response.ok) {
            throw new Error(
                `Failed to upload file to storage (status ${s3Response.status})`
            );
        }

        // 3. Confirm with our API so it records the document. The response shape
        //    matches the previous /api/documents response.
        const confirmed = await postUrl<IDocument[]>('/api/documents/confirm', {
            code,
            key,
            filename: file.name,
            size: file.size,
        });

        uploaded.push(...confirmed);
    }

    // Save response log to store (unchanged behavior).
    const state = store.getState();

    const supportingDocs = state.borrowerReducer.supportingDocs;

    let newSupportingDocs = uploaded;
    Object.keys(supportingDocs).forEach((existingCode) => {
        if (existingCode === code) {
            newSupportingDocs = supportingDocs[existingCode].concat(uploaded);
        }
    });

    const setSupportingDocsPayload: {
        code: string;
        document: ISupportingDoc[];
    } = {
        code,
        document: newSupportingDocs,
    };

    store.dispatch(setSupportingDocs(setSupportingDocsPayload));
    return uploaded;
}
